"use client";

import React from "react";

import { Tab, Tabs } from "@nextui-org/react";
import { useTranslations } from "next-intl";

import { Feature } from "@/components/ui/Feature";
import { PropertyComments } from "@/components/widgets/PropertyComments";
import { PropertyTimeline } from "@/components/widgets/PropertyTimeline";
import { FeatureFlags } from "@/enums/feature-flags";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { cn } from "@/utils/cn";

interface PropertyDetailTabsProps {
  propertyId: string;
  className?: string;
}

export const PropertyDetailTabs: React.FC<PropertyDetailTabsProps> = ({
  propertyId,
  className,
}) => {
  const tRealtor = useTranslations("realtor");

  const enableCommentsTab = useFeatureFlag(FeatureFlags.PROPERTY_COMMENTS);
  const enableHistoryTab = useFeatureFlag(FeatureFlags.PROPERTY_HISTORY);

  const disabledTabs = [];
  if (!enableCommentsTab) {
    disabledTabs.push("comments");
  }
  if (!enableHistoryTab) {
    disabledTabs.push("history");
  }

  return (
    <div className={cn("w-full", className)}>
      <Tabs
        aria-label="history and comments tabs"
        disableAnimation={true}
        size="lg"
        radius="lg"
        classNames={{
          base: "flex justify-center md:justify-start items-center w-full",
          panel: "mt-12",
          tabList: "p-0 bg-white rounded-lg border border-gray-100 shadow shadow-gray-110/25 w-full md:w-auto",
          tab: cn(
            "py-8 px-6 md:py-10 md:px-8 basis-1/2 md:basis-[30rem]",
            "data-[selected=true]:border data-[selected=true]:border-red data-[selected=true]:bg-white",
            "first:data-[selected=true]:rounded-l-lg first:data-[selected=true]:rounded-r-none",
            "last:data-[selected=true]:rounded-r-lg last:data-[selected=true]:rounded-l-none",
          ),
          tabContent: "text-md md:text-lg xl:text-[21px] font-medium text-gray-1200",
        }}
        disabledKeys={disabledTabs}
        defaultSelectedKey={disabledTabs.includes("comments") ? "history" : "comments"}
      >
        <Tab
          key="comments"
          title={tRealtor("metadataComments")}
        >
          <Feature name={FeatureFlags.PROPERTY_COMMENTS}>
            <Feature.Enabled>
              <PropertyComments propertyId={propertyId} />
            </Feature.Enabled>
          </Feature>
        </Tab>

        <Tab
          key="history"
          title={tRealtor("timelinePropertyHistory")}
        >
          <Feature name={FeatureFlags.PROPERTY_HISTORY}>
            <Feature.Enabled>
              <PropertyTimeline propertyId={propertyId} />
            </Feature.Enabled>
          </Feature>
        </Tab>
      </Tabs>
    </div>
  );
};

export default PropertyDetailTabs;
